import { useRoom } from '@peterddod/phop';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { baseRuleSet } from '@/game/rules';
import { DEFAULT_MATCH_SETTINGS, type MatchSettings, resolveMatch } from '@/game/sim';
import { createFrameStore } from '@/game/view/frame';
import type { Names } from '@/game/view/names';
import { displayName } from '@/lib/names';
import { useNetworkedSimRunner } from '../NetworkedSimRunner';

/**
 * The match this screen plays: every peer resolves the same settings, room
 * and roster into the same match and builds the same island from it, runs the
 * networked sim on it, and reads it through one frame store
 * (game/view/frame.ts), published on every sim change.
 */
export function useMatchFrames(
  players: string[] | undefined,
  settings: MatchSettings | undefined,
  /** Players' names by id (RoomGame), read live by `Frame.playerName`. */
  names: ReadonlyMap<string, string> | undefined,
  /** The player's own unit, group and city names (client-local), read live by `Frame.names`. */
  entityNames?: () => Names
) {
  const { roomId, peerId } = useRoom();
  const namesRef = useRef(names);
  namesRef.current = names;
  const playerName = useCallback((id: string) => displayName(id, namesRef.current?.get(id)), []);
  // Frozen match roster from RoomGame; falls back to a solo roster so the
  // screen still works when mounted without one (dev/solo path).
  const roster = useMemo(
    () => (players && players.length > 0 ? players : [peerId]),
    [players, peerId]
  );
  // Length, map radius and seed.
  const match = useMemo(
    () => resolveMatch(settings ?? DEFAULT_MATCH_SETTINGS, roomId, roster.length),
    [settings, roomId, roster.length]
  );
  const runner = useNetworkedSimRunner({ match, players: roster });
  const { getState, playerId, tickCount, terrain } = runner;
  const frames = useMemo(
    () =>
      createFrameStore({
        getState,
        playerId,
        playerOrder: roster,
        playerName,
        names: entityNames,
        rules: baseRuleSet,
        terrain,
      }),
    [getState, playerId, roster, playerName, entityNames, terrain]
  );
  // Layers and panels subscribed to the frame re-render after the screen.
  useLayoutEffect(() => frames.publish(tickCount), [frames, tickCount]);
  return { frames, mapRadius: match.mapRadius, dispatch: runner.dispatch, isHost: runner.isHost };
}
